import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api, usdToCents } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { MultiImageUpload } from '@/components/ImageUpload';

export default function ServiceCreatePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', category: '', price_type: 'hourly', priceUsd: '', photos_base64: [], keywords: '',
    service_areas: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/catalog/categories'), api.get('/catalog/cities')]).then(([c, ci]) => {
      setCategories(c.data.items || []); setCities(ci.data.items || []);
    });
  }, []);

  useEffect(() => { if (user && user.role !== 'jobeur') { toast.error('Réservé aux jobeurs'); nav('/'); } }, [user, nav]);

  const submit = async () => {
    if (!form.title || !form.description || !form.category) return toast.error('Champs requis');
    const price_cents = usdToCents(form.priceUsd);
    if (price_cents <= 0) return toast.error('Tarif requis');
    setSaving(true);
    try {
      await api.post('/services', {
        title: form.title,
        description: form.description,
        category: form.category,
        price_type: form.price_type,
        price_cents,
        photos_base64: form.photos_base64,
        keywords: form.keywords.split(',').map((s) => s.trim()).filter(Boolean),
        service_areas: form.service_areas,
      });
      toast.success('Service publié');
      nav('/dashboard');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  const toggleArea = (name) => {
    setForm((s) => ({
      ...s,
      service_areas: s.service_areas.includes(name) ? s.service_areas.filter((x) => x !== name) : [...s.service_areas, name],
    }));
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="service-create-page">
      <h1 className="text-2xl font-bold">Publier un service</h1>
      <Card className="p-4 space-y-4">
        <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Plombier expérimenté — interventions rapides" data-testid="service-title" /></div>
        <div>
          <Label>Catégorie</Label>
          <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="service-category">
            <option value="">Choisir…</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
        </div>
        <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="service-description" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Type de tarif</Label>
            <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.price_type} onChange={(e) => setForm({ ...form, price_type: e.target.value })} data-testid="service-price-type">
              <option value="hourly">Par heure</option>
              <option value="fixed">Forfait</option>
            </select>
          </div>
          <div><Label>Tarif ($)</Label><Input type="number" step="0.01" value={form.priceUsd} onChange={(e) => setForm({ ...form, priceUsd: e.target.value })} data-testid="service-price" /></div>
        </div>
        <div><Label>Mots-clés (séparés par des virgules)</Label><Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="fuite, évier, chauffe-eau" data-testid="service-keywords" /></div>
        <div>
          <Label>Photos du service</Label>
          <MultiImageUpload values={form.photos_base64} onChange={(v) => setForm({ ...form, photos_base64: v })} max={5} testid="service-photos" />
        </div>
        <div>
          <Label>Zones desservies (villes/communes)</Label>
          <div className="max-h-40 overflow-y-auto border border-[#E2E8F0] rounded-lg p-2 space-y-1">
            {cities.map((c) => (
              <div key={c.name}>
                <button type="button" onClick={() => toggleArea(c.name)} className={`text-xs px-2 py-0.5 rounded-full mr-1 mb-1 ${form.service_areas.includes(c.name) ? 'bg-[#1677F2] text-white' : 'bg-[#EAF3FF] text-[#0E3F80] border border-[#C7E2FF]'}`}>
                  {c.name}
                </button>
                {c.communes.map((com) => (
                  <button key={com} type="button" onClick={() => toggleArea(com)} className={`text-[11px] px-1.5 py-0.5 rounded-full mr-1 mb-1 ${form.service_areas.includes(com) ? 'bg-[#0F5FCC] text-white' : 'bg-slate-100 text-slate-700 border border-slate-200'}`}>
                    {com}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-1">Sélectionnés: {form.service_areas.length}</p>
        </div>
        <Button onClick={submit} disabled={saving} className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="service-submit-btn">
          {saving ? 'Publication…' : 'Publier mon service'}
        </Button>
      </Card>
    </div>
  );
}
